import { useEffect } from 'react'
import { useParams } from 'react-router'
import { useDispatch } from 'react-redux'

// Actions
import { actions as audioActions } from 'modules/audio/actions'
import { actions as browseActions } from 'modules/browse/actions'
import { actions as labelsActions } from 'modules/labels/actions'

// Components
import { Error, Layout } from '@tracklib/kit'
import { ContentHeader } from 'kit'
import InfoItem2 from 'components/base/infoitem2'
import SEO from 'components/base/seo'
import TrackList from 'components/browse/tracklist'
import Filter from 'components/filter'

// Hooks
import { useLabels } from 'modules/entities/hooks'
import { useSearchQuery } from 'modules/browse/hooks'
import { useRequest } from 'modules/requests/hooks'

//  Utils
import { isBrowser } from '@process'
import { createSearchQuery } from 'pages/collection'

// Types
import { Dispatch } from 'types'

export const loadInitialData =
  ({
    location: {
      params: { slug },
    },
  }: {
    location: { params: { slug: string } }
  }) =>
  async (dispatch: Dispatch) => {
    const searchQuery = createSearchQuery({ label: slug }, '')
    return Promise.all([dispatch(labelsActions.fetchLabelInfo(slug)), dispatch(browseActions.fetchTracksBySearchQuery(searchQuery))])
  }

const LabelPage = () => {
  const dispatch = useDispatch()
  const { slug: labelSlug } = useParams()
  const slug = labelSlug || ''

  const queryObj = { label: slug }
  const songListProps = useSearchQuery(queryObj, slug)
  const playAllRequest = useRequest('PREPARE_PLAY_SEARCH_QUERY')
  const fetchLabelInfoRequest = useRequest('FETCH_LABEL_INFO', slug)
  const labels = useLabels()
  const label = Object.keys(labels || {})
    .map(id => labels[id])
    .find(x => x.slug === String(slug))
  const parent = label?.parent
  useEffect(() => {
    if (!label && !fetchLabelInfoRequest.isLoading && !fetchLabelInfoRequest.hasError) {
      dispatch(labelsActions.fetchLabelInfo(slug))
    }
  }, [dispatch, label, fetchLabelInfoRequest, slug])

  if (!label || !slug) {
    if (fetchLabelInfoRequest.isDone && isBrowser) {
      return <Error message="Label couldn't be found" />
    }
  }

  const breadcrumbs = label && [
    {
      text: 'Labels',
      link: '/labels/',
    },
    {
      text: label.name,
      link: `/genres/${label.slug}`,
    },
  ]

  if (parent && breadcrumbs) {
    breadcrumbs.splice(1, 0, {
      text: parent.name,
      link: parent.slug ? `/label/${parent.slug}` : `/tracks/?labels=${parent.id}`,
    })
  }

  return (
    <Layout.FullWidth>
      {label ? (
        <SEO
          title={`${label.name} on Tracklib | Sample Instantly`}
          description={`${label.name}, a label on Tracklib`}
          image={label.cover}
          type="music:label"
        />
      ) : (
        <SEO title="Label | Tracklib" />
      )}
      {label ? (
        <ContentHeader
          playAll={() => dispatch(audioActions.playSearchQuery({ label: slug }))}
          playAllRequest={playAllRequest}
          image={label.cover}
          type="Label"
          title={label.name}
          description={label.description}
          infoItems={[...(parent ? [<InfoItem2 title="Parent label" labels={[parent]} />] : [])]}
          sharePath={`/label/${slug}`}
          adminEditPath={`/tracks/label/${label.id}/change`}
          breadcrumbs={breadcrumbs}
        />
      ) : (
        <ContentHeader loading />
      )}
      <Filter queryObj={queryObj} />
      <TrackList {...songListProps} />
    </Layout.FullWidth>
  )
}

export default LabelPage
